import { 
  Box, 
  CircularProgress, 
  Pagination, 
  Stack, 
  Typography,
  Paper,
  useTheme,
  styled,
  Button,
  Divider
} from "@mui/material";
import { 
  addCategory, 
  deleteCategory, 
  fetchCategories, 
  setPage, 
  updateCategory 
} from "../../Reducer/categoriesSlice";
import AddCategoryDialog from "./AddCategoryDialog";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import CategoryTable from "./CategoryTable";
import FilterControls from "./FilterControls";
import CategoryInfoModal from "./CategoryInfoModal";
import { toast } from 'react-toastify';
import { Add, Refresh } from '@mui/icons-material';

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  }
}));

const StyledPaginationContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2)
}));

const Categories = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { categories, loading, total, page, limit } = useSelector((state) => state.categories);

  const [filterType, setFilterType] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, filterType, limit]);

  const fetchData = () => {
    dispatch(fetchCategories({ page, limit, type_name: filterType }))
      .unwrap()
      .catch((err) => {
        toast.error(`Error fetching categories: ${err?.message || 'An unexpected error occurred'}`);
      });
  };

  const handleRefresh = () => {
    fetchData();
    toast.info('Categories refreshed');
  };

  const handlePageChange = (_, value) => dispatch(setPage(value));
  const pageCount = Math.ceil(total / limit);

  const handleSubmitCategory = (type_name) => {
    const action = editData
      ? updateCategory({ id: editData.id, updatedData: { type_name } })
      : addCategory({ type_name });

    dispatch(action)
      .unwrap()
      .then(() => {
        fetchData();
        toast.success(`Category ${editData ? 'updated' : 'added'} successfully`);
        setEditData(null);
      })
      .catch((error) => {
        toast.error(`Error ${editData ? 'updating' : 'adding'} category: ${error?.message || 'Something went wrong'}`);
      });
  };

  const handleEdit = (category) => {
    setEditData(category);
    setOpenDialog(true);
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setOpenView(true);
  };

  const handleCloseModal = () => {
    setOpenView(false);
    setSelectedCategory(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      dispatch(deleteCategory(id))
        .unwrap()
        .then(() => {
          fetchData();
          toast.success('Category deleted successfully');
        })
        .catch((error) => {
          const message = error?.response?.data?.message ||
            error?.message ||
            'An error occurred while deleting the category.';
          toast.error(`Error deleting category: ${message}`);
        });
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper
      }}>
        <StyledHeader>
          <Box>
            <Typography 
              variant="h4" 
              fontWeight="700" 
              color="primary"
              gutterBottom
            >
              Category Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and organize your product categories
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{
                borderRadius: theme.shape.borderRadius,
                textTransform: 'none'
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add New Type/>}
              onClick={() => {
                setEditData(null);
                setOpenDialog(true);
              }}
              sx={{
                borderRadius: theme.shape.borderRadius,
                textTransform: 'none'
              }}
            >
              Add Category
            </Button>
          </Box>
        </StyledHeader>

        <Divider sx={{ my: 3 }} />

        <FilterControls
          filterType={filterType}
          onFilterChange={setFilterType}
        />
      </Paper>

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: 300 
        }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Paper elevation={0} sx={{ 
            borderRadius: theme.shape.borderRadius,
            overflow: 'hidden',
            mb: 3
          }}>
            <CategoryTable
              categories={categories}
              page={page}
              limit={limit}
              onUpdate={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
            />
          </Paper>

          <StyledPaginationContainer elevation={3}>
            <Pagination 
              count={pageCount} 
              page={page} 
              onChange={handlePageChange} 
              color="primary"
              shape="rounded"
              size="large"
            />
            <Typography variant="body1" color="text.secondary">
              Showing {categories.length} of {total} categories
            </Typography>
          </StyledPaginationContainer>
        </>
      )}

      <AddCategoryDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditData(null);
        }}
        onSubmit={handleSubmitCategory}
        initialData={editData}
      />
      
      <CategoryInfoModal
        open={openView}
        onClose={handleCloseModal}
        category={selectedCategory}
      />
    </Box>
  );
};

export default Categories;